import React from 'react';
import { RSButton, RSInput, RSTextarea } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../MerlinSelect';
import { BUILTIN_DATA_TYPES } from '../../stores/model/metamodel/data_type';
import Styles from '../../styles/colors';
import type { NameInputResult } from '../../merode/controllers/Controller';
import { asRSChange, rsValue, type RSInputChange, type RSTextareaChange } from './rsForm';

/** The editable attribute property keys. */
type AttributeKey = 'name' | 'typeName' | 'description' | 'implementation' | 'validation';

/** A single option in the attribute-type dropdown. */
interface AttributeTypeOption {
  label: string;
  value: string;
}

/** The (possibly-pending) attribute property values shown in the form. */
interface EditAttributeFormProperties {
  name: string | null;
  /** The name of the attribute type. */
  typeName: string | null;
  description: string | null;
  implementation: string | null;
  validation: string | null;
}

interface EditAttributeFormProps {
  /** Callback to close the modal. */
  onRequestClose?: () => void;
  /** Callback to cancel editing (only set when adding a new attribute). */
  onRequestCancel?: (() => void) | null;
  /** The properties of the attribute. */
  properties: EditAttributeFormProperties;
  /** Is the attribute currently valid? Derived from a truthy validation chain. */
  valid: string | boolean | null;
  /** Callback to check the name input. */
  checkName: (name: string) => NameInputResult;
  /** On change handler. Arguments: key, value. */
  onChange: (key: AttributeKey, value: string) => void;
  /** The options for the attribute type dropdown. */
  attributeTypeOptions: AttributeTypeOption[];
}

/**
 * (Controlled) form for editing an attribute
 *
 * Props:
 * - onRequestClose: callback to close the modal
 * - onRequestCancel: callback to cancel editing
 * - properties: the properties of the attribute.
 *    - name
 *    - typeName: the name of the attribute type
 *    - description
 *    - implementation
 *    - validation
 * - valid: is the attribute currently valid?
 * - checkName: callback to check the name
 * - onChange: on change handler. Arguments: key, value
 * - attributeTypeOptions: the options for the attribute type dropdown
 *
 * Not wrapped in observer(): the class wasn't either. It is a fully controlled
 * form driven entirely by props.
 */
export default function EditAttributeForm({
  onRequestClose,
  onRequestCancel,
  properties,
  valid,
  checkName,
  onChange,
  attributeTypeOptions,
}: EditAttributeFormProps) {
  const { name, typeName, description, implementation, validation } = properties;

  /**
   * onSubmit handler to make 'enter' key work.
   */
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (valid && onRequestClose) {
      onRequestClose();
    }
  };

  const renderAttributeTypeSelect = () => {
    // Check the name of the type, but only if it is not a builtin. `typeName` is
    // `string | null`; the builtin lookup and the controller (via `isEmpty`)
    // both handle `null` at runtime — cast at these boundaries.
    let check: { ok: boolean; message?: string };
    if (BUILTIN_DATA_TYPES.includes(typeName as string)) {
      // 'Long text' is not legal java identifier;
      check = { ok: true };
    } else {
      check = checkName(typeName as string);
    }

    return (
      <div
        className={`rs-element rs-input-container rs-input ${check.ok ? '' : 'rs-input--danger'}`}
      >
        <label className="rs-element rs-form-label">Type</label>
        <MerlinSelect
          creatable
          promptTextCreator={(label: string) => `${label} (New)`}
          name="type"
          options={attributeTypeOptions}
          value={rsValue(typeName)}
          clearable={false}
          placeholder=""
          onChange={(option: MerlinSelectOption | null) =>
            onChange('typeName', option != null ? (option.value as string) : '')
          }
        />
        {check.ok ? null : (
          <span className="rs-input__text">
            <p>{check.message}</p>
          </span>
        )}
      </div>
    );
  };

  // `name` is the pending value `string | null`; the controller handles `null`
  // via `isEmpty`. Cast at the call boundary — runtime value unchanged.
  const nameCheck = checkName(name as string);
  return (
    <div className="merlin-modal edit-attribute-form min-w-[400px]">
      <div className="title">Edit attribute</div>
      <form className="form" onSubmit={onSubmit}>
        <div className="modal-body">
          <div className="container">
            <div className="row small-gutters">
              <div className="col-2">
                <RSInput
                  label="Name"
                  value={rsValue(name)}
                  onChange={asRSChange<RSInputChange>((value: string) => onChange('name', value))}
                  level={rsValue(nameCheck.ok ? null : 'danger')}
                  text={rsValue(nameCheck.ok ? null : nameCheck.message)}
                  autoFocus
                />
              </div>

              <div className="col-2">{renderAttributeTypeSelect()}</div>
            </div>
            <div className="row">
              <div className="col">
                <RSTextarea
                  label="Description"
                  value={rsValue(description)}
                  onChange={asRSChange<RSTextareaChange>((value: string) => onChange('description', value))}
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <RSTextarea
                  label="Validation rule"
                  value={rsValue(validation)}
                  onChange={asRSChange<RSTextareaChange>((value: string) => onChange('validation', value))}
                />
              </div>
            </div>
            <div className="row">
              <div className="col">
                <RSTextarea
                  label="Implementation"
                  value={rsValue(implementation)}
                  onChange={asRSChange<RSTextareaChange>((value: string) => onChange('implementation', value))}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="button-wrapper">
          {onRequestCancel && (
            <RSButton
              type="button"
              color={Styles.darkTextColor}
              background={Styles.defaultGradient}
              value="Cancel"
              onClick={onRequestCancel}
            />
          )}
          <RSButton
            background={Styles.blueGradient}
            type="submit"
            value="Close"
            disabled={!valid}
          />
        </div>
      </form>
    </div>
  );
}
